import * as amplitude from '@amplitude/analytics-browser';
import { useContext } from 'react';

import type { UserContextValue, ConfigContextValue } from '@core/context';
import { UserContext, ConfigContext } from '@core/context';

import useEnvInfo from '../useEnvInfo';
import useLocalStorage from '../useLocalStorage';

/** Keep track of whether Amplitude has been initialized */
let isAmplitudeReady = false;

const LOCAL_STORAGE_PREFIX = 'amplitude';
const LOCAL_STORAGE_DEVICE_ID = 'deviceId';

/**
 * Initialize Amplitude Analytics once and only once on the client side if it's
 * enabled and has a valid API key. Returns `true` if Amplitude was successfully
 * initialized or `false` if disabled.
 */
export default function useSetupAmplitude() {
  const env = useEnvInfo();
  const storage = useLocalStorage({ prefix: LOCAL_STORAGE_PREFIX, json: true });

  const user = useContext(UserContext) as unknown as UserContextValue;

  // The shape of user context differs between hub and dash
  const email = user.user?.email || user.email;

  const {
    amplitude: { apiKey: amplitudeApiKey, enabled: amplitudeEnabled } = {
      apiKey: '',
      enabled: false,
    },
  } = useContext(ConfigContext) as ConfigContextValue;

  // Don't initialize amplitude on the server.
  if (env.isServer) return false;
  // Don't initialize amplitude if disabled via config.
  if (!amplitudeEnabled) return false;
  // Don't re-initialize amplitude if it's already been initialized.
  if (isAmplitudeReady) return true;

  amplitude.init(amplitudeApiKey, email, {
    defaultTracking: false,
    deviceId: storage.getItem(LOCAL_STORAGE_DEVICE_ID) || undefined,
    autocapture: {
      attribution: false,
      pageViews: false,
      sessions: false,
      formInteractions: true,
      fileDownloads: false,
      elementInteractions: {
        cssSelectorAllowlist: ['a', 'button'],
        actionClickAllowlist: ['div'],
      },
      networkTracking: false,
    },
  });

  // This looks really ugly but this is the only way (for now) for this to
  // work Upon importing, the plugin immediately references `window` but since
  // this hook is SSR-ed, location does not exist and throws an error.
  import('@amplitude/engagement-browser')
    .then(({ plugin: engagementPlugin }) => {
      /**
       * Enable Guides and Surveys
       * @link https://amplitude.com/docs/guides-and-surveys/sdk
       */
      amplitude.add(engagementPlugin());
    })
    .catch(() => {
      // eslint-disable-next-line no-console
      console.error('@amplitude/engagement-browser plugin failed to load');
    });

  // Store devide ID into local storage to retain it between refreshes.
  const deviceId = amplitude.getDeviceId();
  if (deviceId) {
    storage.setItem(LOCAL_STORAGE_DEVICE_ID, deviceId);
  }

  // Mark as ready to prevent multiple initializations.
  isAmplitudeReady = true;
  return true;
}
